/**
 * Subscription Constants
 *
 * Plan tiers, statuses, feature keys, limit keys, pricing, usage limits,
 * AI model mappings, and pack definitions shared across web and mobile apps.
 */

export const PLAN_TIERS = {
  FREE: 'free',
  PAID: 'paid',
}

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INACTIVE: 'inactive',
}

export const PLAN_FEATURES = {
  WIDGETS_ACCESS: 'widgets_access',
  ROUTE_OPTIMIZER: 'route_optimizer',
  FILES_ACCESS: 'files_access',
  AUTOMATIONS: 'automations',
  CUSTOM_BRANDING: 'custom_branding',
  AI_ASSISTANT: 'ai_assistant',
  ADVANCED_REPORTS: 'advanced_reports',
  TEAM_MANAGEMENT: 'team_management',
  SCHEDULING: 'scheduling',
  CHEMICAL_CALCULATOR: 'chemical_calculator',
}

export const PLAN_LIMITS = {
  CLIENTS: 'clients',
  JOBS_PER_MONTH: 'jobs_per_month',
  ESTIMATES_PER_MONTH: 'estimates_per_month',
  INVOICES_PER_MONTH: 'invoices_per_month',
  EMPLOYEES: 'employees',
  STORAGE_MB: 'storage_mb',
  AUTOMATIONS: 'automations',
}

export const BILLING_PLATFORM = {
  STRIPE: 'stripe',
  APPLE: 'apple',
  GOOGLE: 'google',
  NONE: 'none',
}

export const IAP_PRODUCT_IDS = {
  PAID_PLAN_MONTHLY: 'prosperiacrm.proplan.2026',
  EMPLOYEE_SEAT_ADDON: 'prosperiacrm.oneemployeeseat',
}

// --- Pricing ---

export const PLAN_PRICING = {
  PRO_MONTHLY: 3999,
  EMPLOYEE_SEAT_MONTHLY: 999,
}

// --- Usage Limits per Tier ---
// Field names match subscription_plans.limits JSONB keys in the database

export const USAGE_LIMITS = {
  FREE: {
    email_messages: 50,
    sms_messages: 0,
    ai_credits_monthly: 20,
    employees: 1,
    ai_models: ['gemini-2.5-flash'],
  },
  PAID: {
    email_messages: 250,
    sms_messages: 250,
    ai_credits_monthly: 200,
    employees: 2,
    ai_models: ['gemini-2.5-flash', 'gpt-4o', 'claude-sonnet-4-6'],
  },
}

// --- AI Model Credits ---
// User-facing model IDs used in UI, DB, and subscription_plans.limits.ai_models

export const AI_MODEL_CREDITS = {
  'gemini-2.5-flash': { credits: 1, label: '⚡ Quick', tier: 'basic' },
  'gpt-4o': { credits: 3, label: '🧠 Smart', tier: 'mid' },
  'claude-sonnet-4-6': { credits: 5, label: '💎 Pro', tier: 'premium' },
}

// --- AI Model API Mapping ---
// Maps user-facing IDs to actual API model strings.
// Update these when provider model versions change — no other code needs to change.

export const AI_MODEL_API_MAP = {
  'gemini-2.5-flash': 'gemini-2.5-flash-preview-04-17',
  'gpt-4o': 'gpt-4o-2024-08-06',
  'claude-sonnet-4-6': 'claude-sonnet-4-6-20260217',
}

/**
 * Get the actual API model string for a user-facing model ID.
 * @param {string} modelId - User-facing model ID (e.g. 'claude-sonnet-4-6')
 * @returns {string} API model string (e.g. 'claude-sonnet-4-6-20260217')
 */
export const getApiModelId = (modelId) =>
  AI_MODEL_API_MAP[modelId] || modelId

// --- Stripe Product & Price IDs ---

export const STRIPE_IDS = {
  PRO_MONTHLY: {
    product: 'prod_THjvAChWEmdqbH',
    price: 'price_1Ss7VWHBAXwbffJjOuuvtp9K',
  },
  EMPLOYEE_SEAT: {
    product: 'prod_TjynLuJYt2eTJ0',
    price: 'price_1SmUq6HBAXwbffJjo8fhdDNJ',
  },
  SMS_PACK_50: {
    product: 'prod_UIp9gADOVxWsZY',
    price: 'price_1TKDVWHBAXwbffJjCYXsFhmW',
  },
  SMS_PACK_150: {
    product: 'prod_UIpAyI9vURyLNA',
    price: 'price_1TKDWPHBAXwbffJjWUAHLDWw',
  },
  SMS_PACK_500: {
    product: 'prod_UIpBMMXbpkpaRS',
    price: 'price_1TKDWxHBAXwbffJjQkysLlmh',
  },
  EMAIL_PACK_200: {
    product: 'prod_UIpEj5U9EcPZQr',
    price: 'price_1TKDZsHBAXwbffJjLnQDItD8',
  },
  EMAIL_PACK_500: {
    product: 'prod_UIpEy9O3UrQFTm',
    price: 'price_1TKDaUHBAXwbffJjG47wMy3W',
  },
  AI_CREDITS_100: {
    product: 'prod_UIpB57t4fLTsDj',
    price: 'price_1TKDXYHBAXwbffJjGoMLViwG',
  },
  AI_CREDITS_300: {
    product: 'prod_UIpCY7kQU2uNiX',
    price: 'price_1TKDYJHBAXwbffJjHgvdyYyU',
  },
  AI_CREDITS_500: {
    product: 'prod_UIpDfI8Rg0BzbR',
    price: 'price_1TKDYxHBAXwbffJj70vzw885',
  },
}

// --- Add-on Packs (Pro plan only) ---

export const SMS_PACKS = {
  SMALL: { quantity: 50, price_cents: 499, label: '50 SMS', stripe: STRIPE_IDS.SMS_PACK_50 },
  MEDIUM: { quantity: 150, price_cents: 999, label: '150 SMS', stripe: STRIPE_IDS.SMS_PACK_150 },
  LARGE: { quantity: 500, price_cents: 1499, label: '500 SMS — Best Value', stripe: STRIPE_IDS.SMS_PACK_500 },
}

export const EMAIL_PACKS = {
  SMALL: { quantity: 200, price_cents: 299, label: '200 Emails', stripe: STRIPE_IDS.EMAIL_PACK_200 },
  LARGE: { quantity: 500, price_cents: 499, label: '500 Emails', stripe: STRIPE_IDS.EMAIL_PACK_500 },
}

export const AI_CREDIT_PACKS = {
  SMALL: { quantity: 100, price_cents: 299, label: '100 Credits', stripe: STRIPE_IDS.AI_CREDITS_100 },
  MEDIUM: { quantity: 300, price_cents: 699, label: '300 Credits', stripe: STRIPE_IDS.AI_CREDITS_300 },
  LARGE: { quantity: 500, price_cents: 999, label: '500 Credits — Best Value', stripe: STRIPE_IDS.AI_CREDITS_500 },
}

// --- Apple IAP Product IDs ---

export const APPLE_PRODUCT_IDS = {
  PRO_MONTHLY: 'prosperiacrm.proplan.2026',
  EMPLOYEE_SEAT: 'prosperiacrm.oneemployeeseat',
  SMS_PACK_50: 'prosperiacrm.50smsmessages',
  SMS_PACK_150: 'prosperiacrm.150smsmessages',
  SMS_PACK_500: 'prosperiacrm.500smsmessages',
  EMAIL_PACK_200: 'prosperiacrm.200emailcredits',
  EMAIL_PACK_500: 'prosperiacrm.500emailcredits',
  AI_CREDITS_100: 'prosperiacrm.100aicredits',
  AI_CREDITS_300: 'prosperiacrm.300aicredits',
  AI_CREDITS_500: 'prosperiacrm.500aicredits',
}

/**
 * Maps Apple product IDs to pack types and quantities for consumable purchases.
 */
export const APPLE_PACK_MAP = {
  [APPLE_PRODUCT_IDS.SMS_PACK_50]: { pack_type: 'sms', quantity: 50 },
  [APPLE_PRODUCT_IDS.SMS_PACK_150]: { pack_type: 'sms', quantity: 150 },
  [APPLE_PRODUCT_IDS.SMS_PACK_500]: { pack_type: 'sms', quantity: 500 },
  [APPLE_PRODUCT_IDS.EMAIL_PACK_200]: { pack_type: 'email', quantity: 200 },
  [APPLE_PRODUCT_IDS.EMAIL_PACK_500]: { pack_type: 'email', quantity: 500 },
  [APPLE_PRODUCT_IDS.AI_CREDITS_100]: { pack_type: 'ai_credits', quantity: 100 },
  [APPLE_PRODUCT_IDS.AI_CREDITS_300]: { pack_type: 'ai_credits', quantity: 300 },
  [APPLE_PRODUCT_IDS.AI_CREDITS_500]: { pack_type: 'ai_credits', quantity: 500 },
}

/**
 * Check if a product ID is a consumable pack purchase.
 */
export const isPackProduct = (productId) => !!APPLE_PACK_MAP[productId]

/**
 * Check if a product ID is an employee seat subscription.
 */
export const isSeatProduct = (productId) =>
  productId === APPLE_PRODUCT_IDS.EMPLOYEE_SEAT ||
  productId === IAP_PRODUCT_IDS.EMPLOYEE_SEAT_ADDON ||
  productId === GOOGLE_PRODUCT_IDS.EMPLOYEE_SEAT

/**
 * Check if a product ID is a pro/paid subscription.
 */
export const isProSubscription = (productId) =>
  productId === APPLE_PRODUCT_IDS.PRO_MONTHLY ||
  productId === IAP_PRODUCT_IDS.PAID_PLAN_MONTHLY ||
  productId === GOOGLE_PRODUCT_IDS.PRO_MONTHLY

// --- Google Play Product IDs ---


export const GOOGLE_PRODUCT_IDS = {
  PRO_MONTHLY: 'prosperiacrm.proplan.2026',
  EMPLOYEE_SEAT: 'prosperiacrm.oneemployeeseat.2026',
  SMS_PACK_50: 'prosperiacrm.50smsmessages.2026',
  SMS_PACK_150: 'prosperiacrm.150smsmessages',
  SMS_PACK_500: 'prosperiacrm.500smsmessages',
  EMAIL_PACK_200: 'prosperiacrm.200emailcredits',
  EMAIL_PACK_500: 'prosperiacrm.500emailcredits',
  AI_CREDITS_100: 'prosperiacrm.100aicredits',
  AI_CREDITS_300: 'prosperiacrm.300aicredits',
  AI_CREDITS_500: 'prosperiacrm.500aicredits',
}

/**
 * Maps Google Play product IDs to their type and metadata for server-side processing.
 */
export const GOOGLE_PRODUCT_MAP = {
  [GOOGLE_PRODUCT_IDS.PRO_MONTHLY]: {
    type: 'subscription',
    planTier: 'paid',
    offerToken: null, // Set if using Google Play offers/base plans
  },
  [GOOGLE_PRODUCT_IDS.EMPLOYEE_SEAT]: {
    type: 'seat',
    planTier: null,
  },
  [GOOGLE_PRODUCT_IDS.SMS_PACK_50]: {
    type: 'pack',
    packType: 'sms',
    quantity: 50,
  },
  [GOOGLE_PRODUCT_IDS.SMS_PACK_150]: {
    type: 'pack',
    packType: 'sms',
    quantity: 150,
  },
  [GOOGLE_PRODUCT_IDS.SMS_PACK_500]: {
    type: 'pack',
    packType: 'sms',
    quantity: 500,
  },
  [GOOGLE_PRODUCT_IDS.EMAIL_PACK_200]: {
    type: 'pack',
    packType: 'email',
    quantity: 200,
  },
  [GOOGLE_PRODUCT_IDS.EMAIL_PACK_500]: {
    type: 'pack',
    packType: 'email',
    quantity: 500,
  },
  [GOOGLE_PRODUCT_IDS.AI_CREDITS_100]: {
    type: 'pack',
    packType: 'ai_credits',
    quantity: 100,
  },
  [GOOGLE_PRODUCT_IDS.AI_CREDITS_300]: {
    type: 'pack',
    packType: 'ai_credits',
    quantity: 300,
  },
  [GOOGLE_PRODUCT_IDS.AI_CREDITS_500]: {
    type: 'pack',
    packType: 'ai_credits',
    quantity: 500,
  },
}
